import { useState, useEffect, useRef, type ReactNode } from 'react';
import styled from 'styled-components';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Collapse } from '@mui/material';
import { FlexColumn } from '../../Flex';
import { pluralizeString } from '../../../utils/common';
import { CollapseArrowIcon, ToolText } from './toolDisplay';

const HeaderRow = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  cursor: pointer;
  user-select: none;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  font: inherit;
  color: inherit;

  &:hover ${CollapseArrowIcon} {
    opacity: 1;
  }
`;

const Chain = styled(FlexColumn)<{ $withRail: boolean }>`
  gap: 8px;
  ${({ $withRail, theme }) =>
    $withRail &&
    `
    margin-left: 9px;
    padding-left: 16px;
    border-left: 1px solid ${theme.palette.common.gray5};
  `}
`;

type Props = {
  toolCount: number;
  children: ReactNode;
  isCollapsed?: boolean;
};

const ThoughtProcess = ({
  toolCount,
  children,
  isCollapsed = false,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(!isCollapsed);
  const [showHeader, setShowHeader] = useState(isCollapsed);
  const userToggledRef = useRef(false);
  const toolLabel =
    toolCount > 0 ? ` · ${pluralizeString(toolCount, 'tool', 'tools')}` : '';

  useEffect(() => {
    if (userToggledRef.current) return;
    if (isCollapsed) {
      setShowHeader(true);
      setIsExpanded(false);
      return;
    }
    setShowHeader(false);
    setIsExpanded(true);
  }, [isCollapsed]);

  return (
    <FlexColumn>
      {showHeader && (
        <HeaderRow
          onClick={() => {
            userToggledRef.current = true;
            setIsExpanded((prev) => !prev);
          }}
          aria-expanded={isExpanded}
        >
          <CheckCircleRoundedIcon
            sx={{ fontSize: '18px', color: 'common.magenta' }}
          />
          <ToolText>Thought process{toolLabel}</ToolText>
          <CollapseArrowIcon $expanded={isExpanded} />
        </HeaderRow>
      )}
      <Collapse in={isExpanded} unmountOnExit>
        <Chain $withRail={showHeader}>{children}</Chain>
      </Collapse>
    </FlexColumn>
  );
};

export default ThoughtProcess;
